import { di } from 'react-magnetic-di';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type AgentDetails } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';
import {
	fetchScenarioListFromAgg,
	type McpServerNode,
	type McpToolNode,
	type ScenarioNode,
} from '../../services/scenario-list/index.ts';

type UseAgentDetailsControllerProps = {
	agentId: string;
	enabled?: boolean;
} & AssistanceServiceInjectedParams;

export type AgentDetailsWithAggData = AgentDetails & {
	mcpServers?: McpServerNode[];
	mcpTools?: McpToolNode[];
	scenarios?: ScenarioNode[];
};

export type UseAgentDetailsControllerResult = {
	data: AgentDetailsWithAggData | null;
	isFetching: boolean;
	error: ReturnType<typeof useQuery>['error'];
};

/**
 * Fetches the agent details for the given agentId
 * We're enhancing this data with fields that can only be queried from AGG endpoint
 * @todo move all the agent details to AGG endpoint https://product-fabric.atlassian.net/browse/RAGE-1356
 * @param agentId
 */
export const useAgentDetailsController: (
	props: Omit<UseAgentDetailsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseAgentDetailsControllerResult = createHookWithAssistanceService(
	({
		agentId,
		enabled = true,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
	}: UseAgentDetailsControllerProps): UseAgentDetailsControllerResult => {
		di(fetchScenarioListFromAgg);

		const queryResult = useQuery({
			queryKey: ['fetchAgentDetailsWithScenarios', agentId],
			queryFn: async () => {
				// Start both calls in parallel
				const agentDetailsPromise = (async () => {
					experienceTracker.start({
						id: ExperienceName.FETCH_AGENT_BY_AGENT_ID,
						name: ExperienceName.FETCH_AGENT_BY_AGENT_ID,
					});

					const result = await assistanceService.getAgentDetails(agentId);

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENT_BY_AGENT_ID,
					});

					return result;
				})();

				const scenarioPromise = (async () => {
					try {
						experienceTracker.start({
							id: ExperienceName.FETCH_SCENARIO_LIST,
							name: ExperienceName.FETCH_SCENARIO_LIST,
						});

						const result = await fetchScenarioListFromAgg({
							agentId,
							cloudId: siteId,
						});

						experienceTracker.succeed({
							name: ExperienceName.FETCH_SCENARIO_LIST,
						});

						return result;
					} catch (error) {
						// we don't expose the graphql error as return value of this hook
						// pending TODO above to just use GraphQL endpoint instead of 2 calls
						experienceTracker.fail({
							name: ExperienceName.FETCH_SCENARIO_LIST,
							error: error instanceof Error ? error : new Error(String(error)),
							teamName: ROVO_AGENTS_FE_TEAM_NAME,
						});

						return null;
					}
				})();

				// Wait for both to complete in parallel
				const [agentDetails, scenarioResult] = await Promise.all([
					agentDetailsPromise,
					scenarioPromise,
				]);

				const mcpServers: McpServerNode[] =
					scenarioResult?.data?.agentStudio_scenarioListByContainerId?.edges
						?.flatMap((edge) => edge.node?.mcpServers)
						.filter(Boolean) as McpServerNode[];

				const mcpTools: McpToolNode[] =
					(scenarioResult?.data?.agentStudio_scenarioListByContainerId?.edges
						?.flatMap((edge) => edge.node?.mcpTools)
						.filter(Boolean) as McpToolNode[]) ?? [];

				const scenarios: ScenarioNode[] | undefined =
					scenarioResult?.data?.agentStudio_scenarioListByContainerId?.edges
						?.map((edge) => edge.node)
						.filter(Boolean);

				// Combine the results
				return {
					...agentDetails,
					mcpServers,
					mcpTools,
					scenarios,
				};
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENT_BY_AGENT_ID,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled,
		});

		return queryResult;
	},
);

/**
 * Fetches the agent details for the given identityAccountId
 * We're enhancing this data with fields that can only be queried from AGG endpoint
 * However the AGG endpoint needs to be sequentially called because `agentId` needs to be retrieved first
 *
 * @todo move all the agent details to AGG endpoint https://product-fabric.atlassian.net/browse/RAGE-1356
 * @param identityAccountId
 */
export const useAgentDetailsByIdentityAccountId: (
	props: Omit<
		{
			identityAccountId: string;
			enabled?: boolean;
		} & AssistanceServiceInjectedParams,
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => UseAgentDetailsControllerResult = createHookWithAssistanceService(
	({
		identityAccountId,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
		enabled = true,
	}: {
		identityAccountId: string;
		enabled?: boolean;
	} & AssistanceServiceInjectedParams): UseAgentDetailsControllerResult => {
		di(fetchScenarioListFromAgg);

		const queryResult = useQuery({
			queryKey: ['fetchAgentDetails', identityAccountId],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
				});

				const agentDetails =
					await assistanceService.getAgentDetailsByIdentityAccountId(identityAccountId);

				// after receiving the agent details, we get the agentId
				// then we can use it to fetch the scenario list
				// Note: try catch the AGG call so it doesn't use `useQuery`'s `onError`
				// because it will try to be converted to API error and it will mix up the experienceTracker calls
				try {
					experienceTracker.start({
						id: ExperienceName.FETCH_SCENARIO_LIST,
						name: ExperienceName.FETCH_SCENARIO_LIST,
					});

					const scenarioList = await fetchScenarioListFromAgg({
						agentId: agentDetails.id,
						cloudId: siteId,
					});

					experienceTracker.succeed({
						name: ExperienceName.FETCH_SCENARIO_LIST,
					});

					const mcpServers: McpServerNode[] =
						scenarioList.data?.agentStudio_scenarioListByContainerId?.edges
							?.flatMap((edge) => edge.node?.mcpServers)
							.filter(Boolean) as McpServerNode[];

					const mcpTools: McpToolNode[] =
						scenarioList.data?.agentStudio_scenarioListByContainerId?.edges
							?.flatMap((edge) => edge.node?.mcpTools)
							.filter(Boolean) as McpToolNode[];

					const scenarios: ScenarioNode[] | undefined =
						scenarioList.data?.agentStudio_scenarioListByContainerId?.edges
							?.map((edge) => edge.node)
							.filter(Boolean);

					return {
						...agentDetails,
						mcpServers,
						mcpTools,
						scenarios,
					};
				} catch (error) {
					// we don't expose the graphql error as return value of this hook
					// pending TODO above to just use GraphQL endpoint instead of 2 calls
					experienceTracker.fail({
						name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
						error: error instanceof Error ? error : new Error(String(error)),
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});

					return agentDetails;
				}
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled,
		});

		return queryResult;
	},
);
